import { useState, useCallback, KeyboardEvent } from 'react';
import { Container, StarButton } from './InteractiveStarRating.styled';
import type { StarSize } from './InteractiveStarRating.styled';

const STAR_PATH = 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';
const FILLED_COLOR = '#E8B161';
const EMPTY_COLOR = '#E0E0E0';

interface InteractiveStarRatingProps {
  readonly value: number;
  readonly onChange: (rating: number) => void;
  readonly size?: StarSize;
  readonly disabled?: boolean;
  readonly className?: string;
}

export function InteractiveStarRating({
  value,
  onChange,
  size = 'md',
  disabled = false,
  className,
}: InteractiveStarRatingProps) {
  const [hovered, setHovered] = useState(0);

  const displayValue = hovered || value;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        onChange(Math.min(5, value + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        onChange(Math.max(1, value - 1));
      }
    },
    [disabled, onChange, value],
  );

  return (
    <Container
      role="radiogroup"
      aria-label={value ? `Rating: ${value} out of 5 stars` : 'Select a rating'}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => setHovered(0)}
      starSize={size}
      className={className}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <StarButton
          key={star}
          type="button"
          role="radio"
          aria-checked={value === star}
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
          tabIndex={-1}
          starSize={size}
          disabled={disabled}
          onClick={() => !disabled && onChange(star)}
          onMouseEnter={() => !disabled && setHovered(star)}
        >
          <svg viewBox="0 0 24 24" fill={star <= displayValue ? FILLED_COLOR : EMPTY_COLOR}>
            <path d={STAR_PATH} />
          </svg>
        </StarButton>
      ))}
    </Container>
  );
}
